/**
 * Existing-customer match banner + AI vs CRM conflict picker.
 *
 * When linked, Accept only attaches customerId on the Lead — CRM Customer
 * fields are not updated from this intake flow.
 */

import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";

/**
 * @param {{
 *   match: object|null,
 *   loading?: boolean,
 *   onUseCustomer?: () => void,
 *   onUseAi?: () => void,
 *   onSelectCandidate?: (customerId: string) => void,
 *   onClear?: () => void,
 * }} props
 */
const CustomerMatchBanner = ({
  match = null,
  loading = false,
  onUseCustomer,
  onUseAi,
  onSelectCandidate,
  onClear,
}) => {
  if (loading) {
    return (
      <Alert severity="info" icon={<PersonSearchIcon />} data-testid="customer-match-banner">
        Looking up existing customer…
      </Alert>
    );
  }

  if (!match || match.status === "none" || match.status === "cleared") {
    return null;
  }

  if (match.status === "ambiguous") {
    const candidates = Array.isArray(match.candidates) ? match.candidates : [];
    return (
      <Alert
        severity="warning"
        icon={<PersonSearchIcon />}
        data-testid="customer-match-banner"
        sx={{ alignItems: "flex-start" }}
        action={
          onClear ? (
            <Button color="inherit" size="small" onClick={onClear}>
              Unlink
            </Button>
          ) : null
        }
      >
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Ambiguous customer match
          </Typography>
          <Typography variant="body2">
            Phone and email match different CRM customers. Pick one to link on the Lead
            (CRM is not updated from intake), or unlink.
          </Typography>
          <Stack spacing={1}>
            {candidates.map((c) => {
              const snap = c.customerSnapshot || {};
              const id = snap.customerId;
              return (
                <Stack
                  key={id || c.matchedBy}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ sm: "center" }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {c.matchedBy === "email" ? "Email" : "Phone"}:{" "}
                    {snap.name || "Unnamed"}
                    {snap.phone ? ` · ${snap.phone}` : ""}
                    {snap.email ? ` · ${snap.email}` : ""}
                    {id ? ` · ${id}` : ""}
                  </Typography>
                  {onSelectCandidate && id ? (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onSelectCandidate(id)}
                      data-testid={`customer-match-select-${c.matchedBy}`}
                    >
                      Use this customer
                    </Button>
                  ) : null}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Alert>
    );
  }

  if (match.status !== "matched" || !match.customerSnapshot) {
    return null;
  }

  const snap = match.customerSnapshot;
  const conflicts = Array.isArray(match.fieldConflicts) ? match.fieldConflicts : [];
  const unresolved = conflicts.length > 0 && !match.resolution;

  return (
    <Alert
      severity={unresolved ? "warning" : "success"}
      icon={<PersonSearchIcon />}
      data-testid="customer-match-banner"
      sx={{ alignItems: "flex-start" }}
      action={
        onClear ? (
          <Button color="inherit" size="small" onClick={onClear}>
            Unlink
          </Button>
        ) : null
      }
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Existing customer matched
          </Typography>
          <Chip
            size="small"
            label={match.matchedBy === "email" ? "By email" : "By phone"}
            color="primary"
            variant="outlined"
          />
          {match.resolution ? (
            <Chip
              size="small"
              label={
                match.resolution === "use_customer"
                  ? "Using CRM fields on Lead"
                  : match.resolution === "use_ai"
                    ? "Using AI fields · still linked"
                    : "Manual"
              }
            />
          ) : null}
        </Stack>
        <Typography variant="body2">
          {snap.name || "Unnamed"}
          {snap.phone ? ` · ${snap.phone}` : ""}
          {snap.email ? ` · ${snap.email}` : ""}
          {snap.customerId ? ` · ${snap.customerId}` : ""}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Accept links this customer on the Lead only — CRM Customer fields are not
          updated from intake.
        </Typography>
        {unresolved ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              AI values differ from CRM on {conflicts.map((c) => c.field).join(", ")}.
              Choose which values to put on the Lead. Use AI keeps the customer link
              and does not write CRM.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant="contained"
                onClick={onUseCustomer}
                data-testid="customer-match-use-crm"
              >
                Use Customer
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={onUseAi}
                data-testid="customer-match-use-ai"
              >
                Use AI
              </Button>
            </Stack>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {conflicts.map((c) => (
                <Typography key={c.field} variant="caption" color="text.secondary">
                  {c.field}: AI “{c.aiValue}” vs Customer “{c.customerValue}”
                </Typography>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </Alert>
  );
};

export default CustomerMatchBanner;
